import { inject } from '@angular/core';
import { CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { AUTH_ROLES } from '@core/authentication/auth.roles';
import { AuthService } from '@core/authentication/auth.service';
import { OiqUiAlertDialogService } from '@oiq-ui-sdk/ui';
import { CookieService } from 'ngx-cookie-service';

/**
 * Roles allowed into `/v2/reports`. The underwriter role is stored as `UNDERWRITING`.
 * Custom roles are allowed when `masterRole` (string or `{ name }`) is one of these.
 * A per-user `reports` resource grant is also enough — same fallback as `auth.guard.ts`.
 */
export const V2_REPORTS_ACCESS_ROLES: readonly AUTH_ROLES[] = [
  AUTH_ROLES.ONYXIQ_ADMIN,
  AUTH_ROLES.ADMIN,
  AUTH_ROLES.WHITE_LABEL_ADMIN,
  // AUTH_ROLES.UNDERWRITING,
  AUTH_ROLES.SYNDICATOR,
  AUTH_ROLES.MASTER_SYNDICATOR,
  AUTH_ROLES.ACCOUNTING,
];

export const V2_REPORTS_ACCESS_RESOURCE = 'reports';

/** Role name plus a custom role's master role name, when the login payload includes one. */
export function oiqAssignedRoleNames(role: unknown): string[] {
  if (typeof role === 'string' && role) {
    return [role];
  }
  if (!role || typeof role !== 'object') {
    return [];
  }
  const record = role as { name?: unknown; masterRole?: unknown };
  const names: string[] = [];
  if (typeof record.name === 'string' && record.name) {
    names.push(record.name);
  }
  const master = record.masterRole;
  if (typeof master === 'string' && master) {
    names.push(master);
  } else if (master && typeof master === 'object') {
    const masterName = (master as { name?: unknown }).name;
    if (typeof masterName === 'string' && masterName) {
      names.push(masterName);
    }
  }
  return names;
}

export function oiqUserMatchesRolesOrResource(
  roles: unknown,
  allowedRoles: readonly string[],
  hasResource: boolean
): boolean {
  if (hasResource) {
    return true;
  }
  const allowed = new Set<string>(allowedRoles);
  const assigned = Array.isArray(roles) ? roles : [];
  return assigned.some(role => oiqAssignedRoleNames(role).some(name => allowed.has(name)));
}

/**
 * Role-based route guard for v2 routes.
 *
 * Usage in a routing module:
 *   canActivate: [V2RoleGuard(['ADMIN', 'ONYXIQ_ADMIN'], 'reports')]
 *
 * Allowed when a role name or its `masterRole` is in the list, or when `resource`
 * is set and the user has that per-user resource.
 */
export const V2RoleGuard = (allowedRoles: string[], resource?: string): CanActivateFn => {
  return (_route, state: RouterStateSnapshot) => {
    const authService = inject(AuthService);
    const cookieService = inject(CookieService);
    const router = inject(Router);
    const alert = inject(OiqUiAlertDialogService);

    // Not authenticated -> send to login preserving the return url.
    if (!cookieService.get('currentUser') || !authService.isLoggedInSnapshot) {
      localStorage.removeItem('token');
      router.navigate(['/login'], {
        queryParams: { returnUrl: state.url },
      });
      return false;
    }

    const user = authService.getUser();
    const hasResource = !!resource && !!user && authService.userHasAccessToResources(resource);
    if (oiqUserMatchesRolesOrResource(user?.roles, allowedRoles, hasResource)) {
      return true;
    }

    alert.open(
      {
        title: 'Access Denied',
        content: 'You have no permission to access this page. Please contact your administrator.',
        type: 'error',
        buttonBehaviour: 'close',
        buttons: [
          {
            id: 'confirm',
            text: 'OK',
            style: 'primary-solid',
            behaviour: 'close',
          },
        ],
      },
      {
        width: '500px',
        maxWidth: '500px',
      }
    );
    return false;
  };
};
